import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import FlagAT from "@/assets/svg/austria-flag.svg?react";
import FlagCZ from "@/assets/svg/czech-flag.svg?react";
import FlagEN from "@/assets/svg/en-flag.svg?react";
import FlagDE from "@/assets/svg/germany-flag.svg?react";
import FlagPL from "@/assets/svg/poland-flag.svg?react";
import FlagSK from "@/assets/svg/slovakia-flag.svg?react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LOCALES } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const iconsToLocale: Record<string, any> = {
	cs: FlagCZ,
	de: FlagDE,
	"de-AT": FlagAT,
	sk: FlagSK,
	en: FlagEN,
	pl: FlagPL,
};

function getLocaleDisplayName(locale: string) {
	const displayName = new Intl.DisplayNames([locale], {
		type: "language",
	}).of(locale)!;
	return displayName.charAt(0).toLocaleUpperCase() + displayName.slice(1);
}

export default function LocaleToggle() {
	const { i18n } = useTranslation();
	const resolvedLanguage = i18n.resolvedLanguage ?? i18n.language;

	function changeLocale(lng: string) {
		i18n.changeLanguage(lng);
		window.location.reload();
	}

	const currentLocale = useMemo(
		() => ({
			icon: iconsToLocale[resolvedLanguage],
			text: getLocaleDisplayName(resolvedLanguage),
		}),
		[resolvedLanguage]
	);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className={cn(
						"inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
						"text-secondary-foreground hover:bg-accent hover:text-accent-foreground",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
						"disabled:pointer-events-none disabled:opacity-50"
					)}
				>
					<currentLocale.icon className="size-6 rounded-sm" />
					{currentLocale.text}
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{LOCALES.map((locale) => {
					const Icon = iconsToLocale[locale];
					return (
						<DropdownMenuItem key={locale} onClick={() => changeLocale(locale)} className="gap-2">
							<Icon className="size-6 rounded-sm" />
							{getLocaleDisplayName(locale)}
						</DropdownMenuItem>
					);
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
